// frontend/src/components/PricingBreakdown.jsx
//
// Shared pricing breakdown used by BookingPage (sidebar) and
// TourBuilderPage (expedition ledger).
//
// CHANGES FROM PREVIOUS VERSION
// ──────────────────────────────
// - Added useServerPricingLines() hook.
//   Converts the customTourPricingPreview GraphQL response into the
//   lines[] array that <PricingBreakdown> expects.
//   Replaces useTourBuilderPricingLines() for the TourBuilderPage.
//
// - useTourBuilderPricingLines() kept for the summary sidebar
//   (Steps 1–6) where only local state is available and the full
//   server preview hasn't fired yet.
//
// - useBookingPricingLines() unchanged — used by BookingPage.

import React, { useMemo } from 'react';
import { MapPin } from 'lucide-react';

/* ─── Design tokens ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.12)';
const NAVY     = '#080e1a';

/* ─── Helpers ─── */
const parsePrice = (v) =>
  typeof v === 'string' ? parseFloat(v) || 0 : typeof v === 'number' ? v : 0;

const fmt = (n, cur = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: cur,
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0);

const CAT_LABELS = {
  citizen:        'Adult — Citizen',
  resident:       'Adult — Resident',
  non_resident:   'Adult — International',
  child_citizen:  'Child — Citizen',
  child_resident: 'Child — Resident',
  child:          'Child — International',
  infant:         'Infant',
};
const humanCat = (cat) =>
  CAT_LABELS[cat] || cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/* ─────────────────────────────────────────────────────────────
   <PricingBreakdown> — unchanged
   ─────────────────────────────────────────────────────────────*/
export function PricingBreakdown({
  lines = [],
  totals = {},
  theme = 'light',
  showPerPerson = true,
}) {
  const dark = theme === 'dark';
  const cur  = totals.currency || 'USD';

  const c = {
    bg:         dark ? 'transparent'            : 'transparent',
    label:      dark ? 'rgba(255,255,255,0.45)' : '#64748b',
    value:      dark ? '#ffffff'                : '#0f172a',
    muted:      dark ? 'rgba(255,255,255,0.3)'  : '#94a3b8',
    section:    dark ? 'rgba(255,255,255,0.07)' : '#f0ece6',
    divider:    dark ? 'rgba(255,255,255,0.07)' : '#f0ece6',
    totalLabel: dark ? 'rgba(255,255,255,0.4)'  : '#64748b',
    totalValue: dark ? '#ffffff'                : '#0f172a',
    perPerson:  dark ? 'rgba(255,255,255,0.3)'  : '#94a3b8',
  };

  return (
    <div style={{ fontFamily: "'Jost', sans-serif" }}>
      <div className="space-y-2.5 mb-5">
        {lines.map((item, i) => {
          if (item.type === 'divider') return (
            <div key={i} style={{ height: '1px', background: c.divider, margin: '4px 0' }} />
          );

          if (item.type === 'section') return (
            <div key={i}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em',
                               textTransform: 'uppercase', color: c.label }}>
                  {item.label}
                </span>
                {item.total != null && (
                  <span style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em',
                                 textTransform: 'uppercase', color: c.label }}>
                    {fmt(item.total, cur)}
                  </span>
                )}
              </div>
            </div>
          );

          if (item.type === 'park_fee') return (
            <div key={i}>
              {item._showPark && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px',
                              paddingLeft: '12px', marginBottom: '4px', marginTop: '6px' }}>
                  <MapPin size={9} strokeWidth={1.5} color={GOLD} />
                  <span style={{ fontSize: '13px', fontWeight: 500, color: c.label }}>
                    {item.park}
                    {item.migrationZone && (
                      <span style={{ color: GOLD, marginLeft: '4px' }}>
                        · {item.migrationZone}
                      </span>
                    )}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '24px' }}>
                <span style={{ fontSize: '13px', color: c.label, lineHeight: 1.5 }}>
                {item.who} · {item.rateLabel} — {fmt(item.unitAmount, cur)} × {item.guestCount} guests × {item.dayCount} nights
                  {item.vatAmount > 0 && (
                    <span style={{ fontSize: '13px', color: c.muted, marginLeft: '4px' }}>
                      (net) + {fmt(item.vatAmount, cur)} VAT
                    </span>
                  )}
                </span>
                <span style={{ fontSize: '13px', color: dark ? 'rgba(255,255,255,0.7)' : '#475569',
                               flexShrink: 0, marginLeft: '8px' }}>
                  {fmt(item.subtotalGross ?? item.subtotal, cur)}
                </span>
              </div>
            </div>
          );

          if (item.type === 'adjustment') {
            const isDiscount = item.direction === 'discount';
            const colour     = isDiscount ? '#4ade80' : '#f59e0b';
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '15px', color: colour }}>{item.label}</span>
                <span style={{ fontSize: '15px', color: colour }}>
                  {isDiscount ? '−' : '+'}{fmt(parsePrice(item.amount), cur)}
                </span>
              </div>
            );
          }

          return (
            <div key={i}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '13px', fontWeight: 400,
                               color: item.muted ? c.muted : c.label, lineHeight: 1.5 }}>
                  {item.label}
                </span>
                <span style={{ fontSize: '15px', color: item.muted ? c.muted : c.value,
                               flexShrink: 0, marginLeft: '8px' }}>
                  {item.amount == null
                    ? <span style={{ fontSize: '13px', color: c.muted }}>TBC</span>
                    : fmt(parsePrice(item.amount), cur)
                  }
                </span>
              </div>
              {item.sublabel && (
                <p style={{ fontSize: '13px', color: c.muted, marginTop: '1px', lineHeight: 1.4 }}>
                  {item.sublabel}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ borderTop: `1px solid ${c.divider}`, paddingTop: '16px' }}>
        {totals.tax != null && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span style={{ fontSize: '15px', color: c.label }}>
                  {totals.taxLabel || 'VAT'}
                </span>
                {totals.taxNote && (
                  <p style={{ fontSize: '13px', color: c.muted, marginTop: '2px' }}>
                    {totals.taxNote}
                  </p>
                )}
              </div>
              <span style={{ fontSize: '15px', color: c.value }}>
                {fmt(totals.tax, cur)}
              </span>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'baseline', paddingTop: '12px',
                      borderTop: `1px solid ${c.divider}` }}>
          <span style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em',
                         textTransform: 'uppercase', color: c.totalLabel }}>
            {dark ? 'Estimated Total' : 'Total'}
          </span>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif",
                        fontSize: dark ? '38px' : '32px',
                        fontWeight: 300, color: c.totalValue, lineHeight: 1 }}>
              {fmt(totals.total, cur)}
            </p>
            {showPerPerson && totals.perPerson != null && (
              <p style={{ fontSize: '13px', color: c.perPerson, marginTop: '2px' }}>
                {fmt(totals.perPerson, cur)} per person
              </p>
            )}
            {dark && (
              <p style={{ fontSize: '13px', color: c.perPerson, marginTop: '2px' }}>
                {cur} · subject to confirmation
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   HOOK: useServerPricingLines
   ─────────────────────────────────────────────────────────────
   Converts the customTourPricingPreview server response into the
   lines[] array <PricingBreakdown> expects.

   Used in Step 7 (full breakdown) of TourBuilderPage.
   ─────────────────────────────────────────────────────────────*/
   export function useServerPricingLines({ preview, tourists = [], countries = [] }) {
    return useMemo(() => {
      if (!preview) return [];
  
      const lines = [];
      const bd    = preview.breakdown;
      const cur   = preview.currency || 'USD';
  
      /* ── Agency service fee ── */
      if (bd?.agencyService) {
        const svc = bd.agencyService;
  
        let composition = {};
        try {
          composition = typeof svc.touristComposition === 'string'
            ? JSON.parse(svc.touristComposition)
            : svc.touristComposition || {};
        } catch { composition = {}; }
  
        const adultCount  = composition.adult  || 0;
        const childCount  = composition.child  || 0;
        const infantCount = composition.infant || 0;
  
        lines.push({ type: 'section', label: 'Safari Package', total: parsePrice(svc.gross) });
  
        if (adultCount > 0) {
          lines.push({
            type:     'line',
            label:    `${fmt(parsePrice(svc.feePerPerson), cur)} × ${adultCount} adult${adultCount !== 1 ? 's' : ''}`,
            sublabel: 'Accommodation · transfers · guide',
            amount:   parsePrice(svc.feePerPerson) * adultCount,
          });
        }
  
        if (childCount > 0) {
          const adultGross = parsePrice(svc.feePerPerson) * adultCount;
          const childGross = parsePrice(svc.gross) - adultGross;
          const childRate  = childCount > 0 ? childGross / childCount : 0;
          lines.push({
            type:     'line',
            label:    `${fmt(childRate, cur)} × ${childCount} child${childCount !== 1 ? 'ren' : ''}`,
            sublabel: 'Child rate applied',
            amount:   childGross,
          });
        }
  
        if (infantCount > 0) {
          lines.push({
            type:   'line',
            label:  `${infantCount} infant${infantCount !== 1 ? 's' : ''} — complimentary`,
            amount: 0,
            muted:  true,
          });
        }
  
        // Operator season adjustments
        (svc.adjustments || []).forEach(adj => {
          if (adj.amount === 0) return;
          lines.push({
            type:      'adjustment',
            label:     adj.nights
              ? `${adj.name} · ${adj.nights} night${adj.nights !== 1 ? 's' : ''}`
              : adj.name,
            amount:    adj.amount,
            direction: adj.direction,
          });
        });
      }
  
      /* ── Operator season breakdown (per-night detail) ── */
      const opNights = bd?.operatorSeasonBreakdown || [];
      if (opNights.length > 0) {
        lines.push({ type: 'divider' });
        lines.push({ type: 'section', label: 'Operator Season Detail' });
  
        // Group by season name
        const byseason = {};
        for (const n of opNights) {
          const name = n.seasonName || 'Standard';
          if (!byseason[name]) byseason[name] = { name, seasonType: n.seasonType, nights: 0, adjustment: 0 };
          byseason[name].nights     += 1;
          byseason[name].adjustment += parsePrice(n.adjustment);
        }
  
        Object.values(byseason).forEach(s => {
          lines.push({
            type:     'line',
            label:    `${s.name} · ${s.nights} night${s.nights !== 1 ? 's' : ''}`,
            sublabel: s.seasonType,
            amount:   s.adjustment !== 0 ? s.adjustment : null,
            muted:    s.adjustment === 0,
          });
        });
      }
  
      /* ── Park fees ── */
      const parkDays = bd?.parkFees || [];
  
      if (parkDays.length > 0) {
        lines.push({ type: 'divider' });
  
        const byPark = {};
        for (const day of parkDays) {
          const parkName = day.park;
          const zone     = day.migrationZone || null;
          const key      = zone ? `${parkName}||${zone}` : parkName;
  
          if (!byPark[key]) byPark[key] = { park: parkName, zone, cats: {} };
  
          for (const fee of day.fees || []) {
            if (fee.type !== 'entry_fee') continue;
            const cat = fee.visitorCategory;
            if (!byPark[key].cats[cat]) {
              byPark[key].cats[cat] = {
                cat,
                unitAmount:      fee.unitAmount,
                unitAmountGross: fee.unitAmountGross,
                guestCount:      fee.count,
                dayCount:        0,
                subtotal:        0,
                subtotalGross:   0,
                vatAmount:       0,
                nationalities:   fee.nationalities || [],
                currency:        fee.currency,
              };
            }
            byPark[key].cats[cat].subtotal      += parsePrice(fee.subtotal);
            byPark[key].cats[cat].subtotalGross += parsePrice(fee.subtotalGross);
            byPark[key].cats[cat].vatAmount     += parsePrice(fee.vatAmount);
            byPark[key].cats[cat].dayCount      += 1;
          }
        }
  
        const parkTotal = parsePrice(bd?.summary?.statutoryParkFees);
        lines.push({ type: 'section', label: 'Park & Conservation Fees', total: parkTotal });
  
        let lastKey = null;
        Object.entries(byPark).forEach(([key, { park, zone, cats }]) => {
          Object.values(cats).forEach(row => {
            let who;
            if (row.nationalities?.length) {
              who = row.nationalities
                .map(code => countries.find(c => c.code === code)?.name || code)
                .filter(Boolean)
                .join(' & ');
            } else {
              who = humanCat(row.cat);
            }
  
            const rateLabel =
              row.cat === 'citizen'        ? 'Citizen'
            : row.cat === 'resident'       ? 'East Africa'
            : row.cat === 'non_resident'   ? 'International'
            : row.cat === 'child_citizen'  ? 'Child citizen'
            : row.cat === 'child_resident' ? 'Child EA'
            : row.cat === 'child'          ? 'Child intl'
            : row.cat === 'infant'         ? 'Infant'
            : humanCat(row.cat);
  
            lines.push({
              type:            'park_fee',
              park,
              migrationZone:   zone,
              cat:             row.cat,
              unitAmount:      row.unitAmount,
              unitAmountGross: row.unitAmountGross,
              guestCount:      row.guestCount,
              dayCount:        row.dayCount,
              subtotal:        row.subtotal,
              subtotalGross:   row.subtotalGross,
              vatAmount:       row.vatAmount,
              who,
              rateLabel,
              currency:        row.currency,
              _showPark:       key !== lastKey,
            });
            lastKey = key;
          });
        });
  
        const parkVat = parsePrice(bd?.summary?.statutoryParkFeesVat);
        if (parkVat > 0) {
          lines.push({
            type:     'line',
            label:    'Park fees include VAT (18%) per TANAPA schedule',
            amount:   null,
            muted:    true,
            sublabel: `Net: ${fmt(parsePrice(bd?.summary?.statutoryParkFeesNet), cur)} · VAT: ${fmt(parkVat, cur)}`,
          });
        }
  
      } else {
        lines.push({
          type:   'line',
          label:  'Park & conservation fees',
          amount: null,
          muted:  true,
        });
      }
  
      /* ── Experiences ── */
      const expItems = bd?.experiences?.items || [];
      if (expItems.length > 0) {
        lines.push({ type: 'divider' });
        lines.push({
          type:  'section',
          label: 'Experiences & Activities',
          total: parsePrice(bd.experiences.total),
        });
  
        expItems.forEach(exp => {
          lines.push({
            type:     'line',
            label:    exp.name,
            sublabel: `${exp.durationDisplay || ''} · ${fmt(parsePrice(exp.pricePerPerson), cur)}/pp × ${exp.tourists}`,
            amount:   parsePrice(exp.lineTotal),
            muted:    exp.priceOnRequest,
          });
        });
  
        if (bd.experiences.hasPriceOnRequestItems) {
          lines.push({
            type:   'line',
            label:  'Some experiences require operator confirmation',
            amount: null,
            muted:  true,
          });
        }
      }
  
      /* ── Meal plan / board basis ── */
      const bbItems = bd?.boardBasis || [];
      if (bbItems.length > 0) {
        lines.push({ type: 'divider' });
        lines.push({
          type:  'section',
          label: 'Meal Plan',
          total: bbItems.reduce((s, b) => s + parsePrice(b.total), 0),
        });
        bbItems.forEach(b => {
          lines.push({
            type:     'line',
            label:    b.name,
            sublabel: `${fmt(parsePrice(b.ratePerPersonPerNight), cur)}/pp/night × ${b.tourists} guests × ${b.nights} nights`,
            amount:   parsePrice(b.total),
          });
        });
      }
      /* ── Transfers ── */
      if (bd?.transfer) {
        lines.push({ type: 'divider' });
        lines.push({
          type:  'section',
          label: 'Ground Transfers',
          total: parsePrice(bd.transfer.total),
        });
        lines.push({
          type:     'line',
          label:    bd.transfer.name,
          sublabel: `${fmt(parsePrice(bd.transfer.ratePerNight), cur)}/night × ${bd.transfer.nights} nights`,
          amount:   parsePrice(bd.transfer.total),
          muted:    true,
        });
      }

      /* ── Safari vehicle (from the vehicle rate card) ── */
      if (bd?.vehicle) {
        const vTotal = parsePrice(bd.vehicle.total);
        lines.push({ type: 'divider' });
        if (vTotal > 0) {
          lines.push({ type: 'section', label: 'Safari Vehicle', total: vTotal });
          lines.push({
            type:     'line',
            label:    bd.vehicle.description || 'Safari vehicle',
            sublabel: bd.vehicle.charge === 'per_day'
              ? `${fmt(parsePrice(bd.vehicle.rate), cur)}/vehicle/day × ${bd.vehicle.vehicles} × ${bd.vehicle.days} days`
              : `${fmt(parsePrice(bd.vehicle.rate), cur)}/vehicle for the trip × ${bd.vehicle.vehicles}`,
            amount:   vTotal,
            muted:    true,
          });
        } else {
          lines.push({ type: 'line', label: 'Safari vehicle', sublabel: bd.vehicle.note || 'Added in your final quote', amount: null, muted: true });
        }
      }

      /* ── Insurance ── */
      if (bd?.insurance) {
        lines.push({ type: 'divider' });
        lines.push({
          type:  'section',
          label: 'Travel Insurance',
          total: parsePrice(bd.insurance.total),
        });
        lines.push({
          type:     'line',
          label:    bd.insurance.name,
          sublabel: `${fmt(parsePrice(bd.insurance.ratePerPersonPerDay), cur)}/pp/day × ${bd.insurance.tourists} guests × ${bd.insurance.nights} nights`,
          amount:   parsePrice(bd.insurance.total),
          muted:    true,
        });
      }
  
      /* ── Subtotal line ── */
      lines.push({ type: 'divider' });
      lines.push({
        type:   'line',
        label:  'Subtotal (before VAT)',
        amount: parsePrice(preview.subtotal),
      });
  
      return lines;
    }, [preview, tourists, countries]);
  }
/* ─────────────────────────────────────────────────────────────
   HOOK: useSummaryPricingLines
   ─────────────────────────────────────────────────────────────
   Converts the customTourPricingSummary lightweight response
   into lines[] for the sidebar during Steps 1–6.
   Shows estimate label when is_estimate=true.
   ─────────────────────────────────────────────────────────────*/
export function useSummaryPricingLines({ summary, itinerary = [], accommodation = {} }) {
  return useMemo(() => {
    if (!summary) return [];

    const lines = [];
    const cur   = summary.currency || 'USD';

    /* Package estimate */
    lines.push({
      type:     'line',
      label:    summary.isEstimate
        ? 'Safari package (estimate)'
        : 'Safari package',
      sublabel: summary.isEstimate
        ? 'Based on lowest available rate · final price confirmed at Step 7'
        : 'Accommodation · transfers · guide',
      amount:   parsePrice(summary.subtotal),
    });

    /* Operator season adjustments */
    (summary.seasonsApplied || []).forEach(s => {
      if (!s.adjustment || s.adjustment === 0) return;
      const isDiscount = s.adjustment > 0;
      lines.push({
        type:      'adjustment',
        label:     `${s.name}${s.nights ? ` · ${s.nights} nights` : ''}`,
        amount:    Math.abs(s.adjustment),
        direction: isDiscount ? 'discount' : 'surcharge',
      });
    });

    /* Park fees — TBC until Step 3 complete */
    lines.push({
      type:  'line',
      label: 'Park & conservation fees',
      amount: null,
      muted:  true,
    });

    return lines;
  }, [summary, itinerary, accommodation]);
}

/* ─────────────────────────────────────────────────────────────
   HOOK: useBookingPricingLines  — UNCHANGED
   Used by BookingPage (packaged tour booking flow).
   ─────────────────────────────────────────────────────────────*/
export function useBookingPricingLines({ pricing, tourists, countries, tour }) {
  return useMemo(() => {
    if (!pricing) return [];

    const lines = [];

    if (pricing.breakdown?.agency_service) {
      const svc = pricing.breakdown.agency_service;
      lines.push({
        type:  'section',
        label: 'Safari Package',
        total: parsePrice(svc.gross),
      });
      lines.push({
        type:     'line',
        label:    `${fmt(parsePrice(svc.fee_per_person))} per person × ${tourists.length} guest${tourists.length !== 1 ? 's' : ''}`,
        sublabel: 'Accommodation · transport · guide · meals',
        amount:   parsePrice(svc.gross),
      });
    }

    const days          = pricing.breakdown?.park_fees;
    const parkFeesTotal = parsePrice(pricing.breakdown?.summary?.statutory_park_fees);

    if (days?.length) {
      const byPark = {};
      for (const day of days) {
        const parkName = day.park;
        if (!byPark[parkName]) byPark[parkName] = {};
        for (const fee of day.fees || []) {
          if (fee.type !== 'entry_fee') continue;
          const cat = fee.visitor_category;
          if (!byPark[parkName][cat]) {
            byPark[parkName][cat] = {
              cat, unitAmount: fee.unit_amount, guestCount: fee.count,
              subtotal: 0, nationalities: fee.nationalities || [],
            };
          }
          byPark[parkName][cat].subtotal += parsePrice(fee.subtotal);
        }
      }

      lines.push({ type: 'section', label: 'Park & Conservation Fees', total: parkFeesTotal });

      let lastPark = null;
      Object.entries(byPark).forEach(([park, cats]) => {
        Object.values(cats).forEach(row => {
          const parkCountryCode = tour?.parks
            ?.find(p => p.name === park)?.country?.code?.toUpperCase();

          let who;
          if (row.nationalities?.length) {
            who = row.nationalities
              .map(code => countries.find(c => c.code === code)?.name || code)
              .filter(Boolean).join(' & ');
          } else if (row.cat === 'citizen') {
            who = parkCountryCode
              ? (countries.find(c => c.code === parkCountryCode)?.name || parkCountryCode)
              : 'Citizen';
          } else if (row.cat === 'non_resident') {
            const intl  = tourists.filter(t => {
              const cls = (countries.find(c => c.code === t.nationality)?.visitorClassification || '').toLowerCase();
              return !['citizen', 'east_africa', 'east_african', 'resident'].includes(cls);
            });
            const names = [...new Set(intl.map(t => countries.find(c => c.code === t.nationality)?.name || t.nationality))];
            who = names.length ? names.join(' & ') : 'International';
          } else {
            who = humanCat(row.cat);
          }

          const rateLabel =
            row.cat === 'citizen'       ? 'Citizen'
          : row.cat === 'resident'      ? 'East Africa'
          : row.cat === 'non_resident'  ? 'International'
          : row.cat === 'child_citizen' ? 'Child citizen'
          : row.cat === 'child_resident'? 'Child EA'
          : row.cat === 'child'         ? 'Child intl'
          : row.cat === 'infant'        ? 'Infant'
          : humanCat(row.cat);

          lines.push({
            type: 'park_fee', park, cat: row.cat,
            unitAmount: row.unitAmount, guestCount: row.guestCount,
            subtotal: row.subtotal, who, rateLabel,
            _showPark: park !== lastPark,
          });
          lastPark = park;
        });
      });
    } else if (parkFeesTotal > 0) {
      lines.push({ type: 'line', label: 'Park & conservation fees', amount: parkFeesTotal });
    }

    (pricing.breakdown?.agency_service?.adjustments || []).forEach(adj => {
      lines.push({ type: 'adjustment', label: adj.name, amount: adj.amount, direction: adj.direction });
    });

    return lines;
  }, [pricing, tourists, countries, tour]);
}

/* ─────────────────────────────────────────────────────────────
   HOOK: useTourBuilderPricingLines — KEPT for fallback only
   Used during Steps 1–6 when server summary is unavailable.
   ─────────────────────────────────────────────────────────────*/
export function useTourBuilderPricingLines({
  itinerary, accommodation, customAccommodations,
  getCountryProperties, getMealForCountry, getMealPlanObj,
  transfers, transferType, insurance, insuranceOptions,
  flightAssistance, groupSize, totalNights, getCountryColor,
}) {
  return useMemo(() => {
    const lines = [];
    const trans = transfers.find(t => t.id === transferType);

    itinerary.forEach(leg => {
      const allProps = [
        ...getCountryProperties(leg.name),
        ...(customAccommodations[leg.name] || []),
      ];
      const selProp = allProps.find(o => o.id === accommodation[leg.name]);
      const meal    = getMealPlanObj(getMealForCountry(leg.name));
      if (!selProp) return;

      const accCost   = leg.nights * (selProp?.price || 0) * groupSize;
      const mealCost  = leg.nights * (meal?.price    || 0) * groupSize;
      const transCost = leg.nights * (trans?.price   || 0);
      const expCost   = leg.selectedAttractions.reduce((s, a) => s + a.price * groupSize, 0);

      lines.push({ type: 'section', label: leg.name });
      lines.push({
        type:     'line',
        label:    `${selProp.name} · ${leg.nights} nights`,
        sublabel: meal.name,
        amount:   accCost + mealCost,
      });
      if (transCost > 0) lines.push({ type: 'line', label: 'Transfers', amount: transCost, muted: true });
      if (expCost > 0)   lines.push({ type: 'line', label: `${leg.selectedAttractions.length} experience${leg.selectedAttractions.length > 1 ? 's' : ''}`, amount: expCost, muted: true });
    });

    const ins = insuranceOptions.find(i => i.id === insurance);
    if (ins?.price > 0) {
      lines.push({ type: 'divider' });
      lines.push({ type: 'line', label: `Insurance · ${ins.name}`, amount: ins.price * groupSize });
    }
    if (flightAssistance) lines.push({ type: 'line', label: 'Flight assistance', amount: 150 });
    lines.push({ type: 'line', label: 'Park & conservation fees', amount: null, muted: true });

    return lines;
  }, [
    itinerary, accommodation, customAccommodations, transferType,
    insurance, flightAssistance, groupSize, totalNights,
    transfers, insuranceOptions,
  ]);
}